import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

interface PlanTier {
  id: string;
  name: string;
  price: string;
  period: string;
  features: string[];
  highlighted: boolean;
}

const PLANS: PlanTier[] = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: '/month',
    features: [
      'Up to 5 clients',
      'Basic notifications',
      'Community support',
    ],
    highlighted: false,
  },
  {
    id: 'starter',
    name: 'Starter',
    price: '$19',
    period: '/month',
    features: [
      'Up to 25 clients',
      'Invoicing & templates',
      'Email support',
      'Basic analytics',
    ],
    highlighted: false,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '$49',
    period: '/month',
    features: [
      'Unlimited clients',
      'Leads pipeline & bids',
      'Custom branding',
      'Priority support',
      'Advanced analytics',
    ],
    highlighted: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$99',
    period: '/month',
    features: [
      'Everything in Professional',
      'Team member accounts',
      'API access',
      'Dedicated support',
      'Custom integrations',
    ],
    highlighted: false,
  },
];

export function BillingPage() {
  const user = useAuthStore((s) => s.user);
  const currentTier = user?.subscriptionTier ?? 'free';

  return (
    <div>
      {/* Breadcrumb-style nav */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        <Link
          to="/settings"
          style={{
            padding: '8px 16px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-muted)',
            fontSize: '0.8125rem',
            fontWeight: 500,
            textDecoration: 'none',
          }}
        >
          Profile
        </Link>
        <span
          style={{
            padding: '8px 16px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-primary)',
            color: '#FFFDF9',
            fontSize: '0.8125rem',
            fontWeight: 500,
          }}
        >
          Billing
        </span>
      </div>

      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '4px' }}>Billing & Subscription</h1>
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
          Manage your subscription plan and payment methods.
        </p>
      </div>

      {/* Current plan card */}
      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px',
          marginBottom: '32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
            Current Plan
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, textTransform: 'capitalize' }}>
            {currentTier}
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: '4px' }}>
            {currentTier === 'free'
              ? 'Upgrade to unlock invoicing, leads, and more.'
              : 'Your next billing date is March 1, 2026.'}
          </div>
        </div>
        {currentTier !== 'free' && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              style={{
                padding: '8px 16px',
                background: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-text-muted)',
                fontSize: '0.8125rem',
                cursor: 'pointer',
              }}
            >
              Cancel Plan
            </button>
            <button
              style={{
                padding: '8px 16px',
                background: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-text)',
                fontSize: '0.8125rem',
                cursor: 'pointer',
              }}
            >
              Update Payment Method
            </button>
          </div>
        )}
      </div>

      {/* Plans grid */}
      <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '16px' }}>
        Available Plans
      </h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
        }}
      >
        {PLANS.map((plan) => {
          const isCurrent = plan.id === currentTier;
          return (
            <div
              key={plan.id}
              style={{
                background: 'var(--color-surface)',
                border: plan.highlighted
                  ? '2px solid var(--color-primary)'
                  : '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
            >
              {plan.highlighted && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '2px 12px',
                    background: 'var(--color-primary)',
                    color: '#FFFDF9',
                    borderRadius: '999px',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Popular
                </span>
              )}
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                {plan.name}
              </h3>
              <div style={{ marginBottom: '16px' }}>
                <span style={{ fontSize: '2rem', fontWeight: 700 }}>{plan.price}</span>
                <span style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                  {plan.period}
                </span>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1, marginBottom: '20px' }}>
                {plan.features.map((feature) => (
                  <li
                    key={feature}
                    style={{
                      padding: '6px 0',
                      fontSize: '0.8125rem',
                      color: 'var(--color-text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ color: 'var(--color-success)' }}>{'\u2713'}</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                disabled={isCurrent}
                style={{
                  padding: '10px',
                  background: isCurrent
                    ? 'var(--color-surface-hover)'
                    : plan.highlighted
                      ? 'var(--color-primary)'
                      : 'transparent',
                  border: isCurrent || plan.highlighted
                    ? 'none'
                    : '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  color: isCurrent
                    ? 'var(--color-text-muted)'
                    : plan.highlighted
                      ? '#fff'
                      : 'var(--color-text)',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  cursor: isCurrent ? 'default' : 'pointer',
                  width: '100%',
                }}
              >
                {isCurrent ? 'Current Plan' : 'Select Plan'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Payment history */}
      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px',
        }}
      >
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '16px' }}>
          Payment History
        </h2>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              {['Date', 'Description', 'Amount', 'Status'].map((header) => (
                <th
                  key={header}
                  style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--color-text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentTier === 'free' ? (
              <tr>
                <td colSpan={4} style={{ padding: '32px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                  No payment history. Upgrade to a paid plan to get started.
                </td>
              </tr>
            ) : (
              <>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '12px', fontSize: '0.875rem' }}>Feb 1, 2026</td>
                  <td style={{ padding: '12px', fontSize: '0.875rem' }}>Professional Plan - Monthly</td>
                  <td style={{ padding: '12px', fontSize: '0.875rem', fontWeight: 500 }}>$49.00</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{ padding: '3px 8px', borderRadius: '999px', fontSize: '0.75rem', background: 'rgba(34,197,94,0.15)', color: 'var(--color-success)' }}>Paid</span>
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '12px', fontSize: '0.875rem' }}>Jan 1, 2026</td>
                  <td style={{ padding: '12px', fontSize: '0.875rem' }}>Professional Plan - Monthly</td>
                  <td style={{ padding: '12px', fontSize: '0.875rem', fontWeight: 500 }}>$49.00</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{ padding: '3px 8px', borderRadius: '999px', fontSize: '0.75rem', background: 'rgba(34,197,94,0.15)', color: 'var(--color-success)' }}>Paid</span>
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
